import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { servicenowCmdbApi, type CMDBRelationshipItem } from '../api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { RefreshCw, Plus, EyeOff, Trash2, ArrowRight } from 'lucide-react'
import { useCan } from '@/hooks/useCan'

const STATUS_BADGES: Record<string, { label: string; variant: 'info' | 'warning' | 'danger' | 'success' | 'neutral' | 'outline' }> = {
    new: { label: 'New', variant: 'info' },
    synced: { label: 'Mapped', variant: 'success' },
    deleted: { label: 'Deleted', variant: 'danger' },
    ignored: { label: 'Ignored', variant: 'neutral' },
}

export function RelationshipsTab() {
    const { toast } = useToast()
    const queryClient = useQueryClient()
    // Syncing, adding, removing and ignoring a relationship are servicenow.update; the tab itself is servicenow.read.
    const canUpdate = useCan('servicenow.update')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [syncing, setSyncing] = useState(false)

    const { data: statsData } = useQuery({
        queryKey: ['cmdb-relationship-stats'],
        queryFn: async () => {
            const res = await servicenowCmdbApi.getRelationshipStats()
            const d = res.data as any
            return d?.data || d
        },
    })

    const { data: itemsData, isLoading } = useQuery({
        queryKey: ['cmdb-relationships', statusFilter],
        queryFn: async () => {
            const params: any = { limit: 200 }
            if (statusFilter !== 'all') params.status = statusFilter
            const res = await servicenowCmdbApi.getRelationships(params)
            const d = res.data as any
            return d?.data || d
        },
    })

    const syncMutation = useMutation({
        mutationFn: () => servicenowCmdbApi.syncRelationships(),
        onMutate: () => setSyncing(true),
        onSettled: () => setSyncing(false),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-relationships'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-relationship-stats'] })
            toast({ title: 'Relationships synced from ServiceNow' })
        },
        onError: () => {
            toast({ title: 'Relationship sync failed', variant: 'destructive' })
        },
    })

    const addMutation = useMutation({
        mutationFn: (id: number) => servicenowCmdbApi.addRelationship(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-relationships'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-relationship-stats'] })
            toast({ title: 'Mapping added to VaultSentinel' })
        },
        onError: () => {
            toast({ title: 'Failed to add mapping', variant: 'destructive' })
        },
    })

    const ignoreMutation = useMutation({
        mutationFn: (id: number) => servicenowCmdbApi.ignoreRelationship(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-relationships'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-relationship-stats'] })
            toast({ title: 'Relationship ignored' })
        },
    })

    const deleteMutation = useMutation({
        mutationFn: (id: number) => servicenowCmdbApi.deleteRelationshipMapping(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-relationships'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-relationship-stats'] })
            toast({ title: 'Mapping removed from VaultSentinel' })
        },
        onError: () => {
            toast({ title: 'Failed to remove mapping', variant: 'destructive' })
        },
    })

    const stats = statsData || { total: 0, new: 0, synced: 0, deleted: 0, ignored: 0 }
    const items: CMDBRelationshipItem[] = itemsData?.items || []

    const statFilters = [
        { key: 'all', label: 'All', count: stats.total },
        { key: 'new', label: 'New', count: stats.new },
        { key: 'synced', label: 'Mapped', count: stats.synced },
        { key: 'deleted', label: 'Deleted', count: stats.deleted },
        { key: 'ignored', label: 'Ignored', count: stats.ignored },
    ]

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold">CI Relationships</h2>
                    <p className="text-sm text-muted-foreground">
                        Object-to-object dependencies from ServiceNow CMDB
                    </p>
                </div>
                {canUpdate && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => syncMutation.mutate()}
                        disabled={syncing}
                    >
                        <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                        Sync Relationships
                    </Button>
                )}
            </div>

            {/* Stats / filter chips */}
            <div className="flex items-center gap-2 flex-wrap">
                {statFilters.map((f) => (
                    <Button
                        key={f.key}
                        variant={statusFilter === f.key ? 'default' : 'outline'}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setStatusFilter(f.key)}
                    >
                        {f.label}
                        <Badge variant="secondary" className="ml-1 text-[10px]">{f.count}</Badge>
                    </Button>
                ))}
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Loading relationships...</div>
            ) : items.length === 0 ? (
                <Card>
                    <CardContent className="flex items-center justify-center h-32">
                        <p className="text-muted-foreground text-sm">
                            {stats.total === 0
                                ? 'No relationships found. Click "Sync Relationships" to fetch from ServiceNow.'
                                : 'No relationships match the current filter.'}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="rounded-lg border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[80px]">Status</TableHead>
                                <TableHead>Parent (Depends On)</TableHead>
                                <TableHead className="w-[40px]" />
                                <TableHead>Child (Used By)</TableHead>
                                <TableHead>Relationship Type</TableHead>
                                <TableHead>VS Parent ID</TableHead>
                                <TableHead>VS Child ID</TableHead>
                                {canUpdate && <TableHead className="w-[120px]">Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((item) => {
                                const badge = STATUS_BADGES[item.syncStatus] || STATUS_BADGES.new
                                const canAdd = item.syncStatus === 'new' && item.parentObjectId && item.childObjectId
                                const canRemove = item.syncStatus === 'synced'
                                const canIgnore = item.syncStatus === 'new'
                                const missingLink = item.syncStatus === 'new' && (!item.parentObjectId || !item.childObjectId)

                                return (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <Badge variant={badge.variant} className="text-xs">{badge.label}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-medium text-sm">{item.parentCiName || item.parentSysId}</div>
                                        </TableCell>
                                        <TableCell>
                                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-medium text-sm">{item.childCiName || item.childSysId}</div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-sm text-muted-foreground">{item.relationshipTypeName || item.relationshipType || '-'}</span>
                                        </TableCell>
                                        <TableCell>
                                            {item.parentObjectId ? (
                                                <span className="text-xs font-mono">{item.parentObjectId}</span>
                                            ) : (
                                                <span className="text-xs text-muted-foreground italic">Not linked</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {item.childObjectId ? (
                                                <span className="text-xs font-mono">{item.childObjectId}</span>
                                            ) : (
                                                <span className="text-xs text-muted-foreground italic">Not linked</span>
                                            )}
                                        </TableCell>
                                        {canUpdate && (
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                {canAdd && (
                                                    <Button
                                                        size="sm"
                                                        variant="default"
                                                        className="h-7 text-xs px-2"
                                                        onClick={() => addMutation.mutate(item.id)}
                                                        disabled={addMutation.isPending}
                                                    >
                                                        <Plus className="h-3 w-3 mr-1" />Add
                                                    </Button>
                                                )}
                                                {missingLink && (
                                                    <span className="text-[10px] text-yellow-600 dark:text-yellow-400">Link CIs first</span>
                                                )}
                                                {canRemove && (
                                                    <Button
                                                        size="sm"
                                                        variant="destructive"
                                                        className="h-7 text-xs px-2"
                                                        onClick={() => deleteMutation.mutate(item.id)}
                                                        disabled={deleteMutation.isPending}
                                                    >
                                                        <Trash2 className="h-3 w-3 mr-1" />Remove
                                                    </Button>
                                                )}
                                                {canIgnore && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-7 text-xs px-2"
                                                        onClick={() => ignoreMutation.mutate(item.id)}
                                                    >
                                                        <EyeOff className="h-3 w-3 mr-1" />Ignore
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                        )}
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    )
}
